import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Check, Copy } from 'lucide-react';
import { useState, type MouseEvent } from 'react';

export type SkuCopyButtonProps = {
  sku: string;
  className?: string;
};

export function SkuCopyButton({ sku, className }: SkuCopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const value = sku.trim();

  if (!value) return null;

  const handleCopy = async (e: MouseEvent) => {
    e.stopPropagation();

    try {
      if (!navigator.clipboard || !window.isSecureContext) {
        throw new Error('Clipboard API not available');
      }
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
      toast({
        title: 'Copied!',
        description: `SKU "${value}" copied to clipboard.`,
      });
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Unable to copy the SKU. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      onPointerDown={(e) => e.stopPropagation()}
      title="Click to copy SKU"
      aria-label={`Copy SKU ${value}`}
      className={cn(
        'group flex w-full min-h-6 items-center justify-between gap-2 rounded-md px-2 py-1 touch-manipulation',
        'text-[10px] font-medium leading-snug transition-colors active:scale-[0.98]',
        copied
          ? 'bg-blue-700 text-white'
          : 'bg-blue-600 text-white hover:bg-blue-700',
        className,
      )}
    >
      <span className="min-w-0 flex-1 break-words font-mono text-left">{value}</span>
      {copied ? (
        <Check className="h-2.5 w-2.5 shrink-0" aria-hidden />
      ) : (
        <Copy
          className="h-2.5 w-2.5 shrink-0 opacity-80 transition-opacity group-hover:opacity-100"
          aria-hidden
        />
      )}
    </button>
  );
}
